<template>
	<div class="not-found-shell">
		<fruit-rain :density="34" :always-on="true" />

		<div class="floating floating-left">
			<img src="../assets/pizza.png" alt="" aria-hidden="true" />
		</div>
		<div class="floating floating-right">
			<img src="../assets/salad2.png" alt="" aria-hidden="true" />
		</div>

		<div class="container py-5">
			<div class="not-found-card mx-auto">
				<div class="eyebrow">404 · plate not found</div>
				<h1>Well, this page wandered off the meal plan.</h1>
				<p class="lead-copy">
					We checked the pantry, the fridge, and that one container in the back
					with mysterious intentions. This route does not exist.
				</p>

				<div class="crumb-joke">
					<span>Possible causes:</span>
					<span>a stale link</span>
					<span>a typo</span>
					<span>an extremely ambitious breadcrumb trail</span>
				</div>

				<div class="action-row">
					<router-link class="btn btn-orange btn-lg" :to="primaryLink.to">
						{{ primaryLink.label }}
					</router-link>
					<router-link class="btn btn-outline-dark btn-lg" to="/">
						Go Home
					</router-link>
				</div>

				<div class="quick-links">
					<router-link to="/add">Log a Meal</router-link>
					<router-link to="/search">Search</router-link>
					<router-link to="/pricing">Pricing</router-link>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup>
	import { computed } from "vue"
	import FruitRain from "@/components/FruitRain.vue"
	import { useTokenStore } from "@/stores/token"

	const tokenStore = useTokenStore()

	const primaryLink = computed(() => {
		if (tokenStore.hasToken()) {
			return { to: "/dashboard", label: "Back to Dashboard" }
		}

		return { to: "/signin", label: "Head to Sign In" }
	})
</script>

<style lang="less" scoped>
	@import "../assets/colors.less";

	.not-found-shell {
		min-height: 100vh;
		position: relative;
		overflow: hidden;
		background: transparent;
		display: flex;
		align-items: center;
		padding-inline: 1rem;
	}

	:deep(.fruit-rain-canvas) {
		z-index: 0;
	}

	.not-found-card {
		max-width: 860px;
		padding: 2.5rem 2rem;
		background: rgba(255, 251, 245, 0.68);
		backdrop-filter: blur(12px);
		-webkit-backdrop-filter: blur(12px);
		border: 1px solid rgba(255, 255, 255, 0.5);
		border-radius: 32px;
		box-shadow: 0 22px 70px rgba(0, 0, 0, 0.28);
		position: relative;
		z-index: 2;
		text-align: center;
	}

	.eyebrow {
		display: inline-block;
		padding: 0.45rem 0.8rem;
		border-radius: 999px;
		background: fade(@color4, 14%);
		color: darken(@color4, 8%);
		text-transform: uppercase;
		letter-spacing: 0.12em;
		font-weight: 800;
		font-size: 0.78rem;
		margin-bottom: 1rem;
	}

	h1 {
		font-size: clamp(2.4rem, 5vw, 4.7rem);
		line-height: 0.95;
		font-weight: 900;
		color: #35160b;
		max-width: 12ch;
		margin: 0 auto 1.1rem;
	}

	.lead-copy {
		font-size: 1.1rem;
		line-height: 1.7;
		color: #6a4637;
		max-width: 50ch;
		margin: 0 auto 1.5rem;
	}

	.crumb-joke {
		display: flex;
		flex-wrap: wrap;
		gap: 0.65rem;
		justify-content: center;
		margin: 0 auto 2rem;
		max-width: 700px;
	}

	.crumb-joke span {
		padding: 0.65rem 0.9rem;
		border-radius: 999px;
		background: rgba(255, 255, 255, 0.85);
		border: 1px solid rgba(110, 74, 48, 0.1);
		color: #6a4637;
		font-weight: 600;
	}

	.action-row {
		display: flex;
		flex-wrap: wrap;
		justify-content: center;
		gap: 0.9rem;
		margin-bottom: 1.25rem;
	}

	.btn-orange {
		background-color: @color4;
		border-color: @color4;
		color: white;
		font-weight: 700;
		padding-inline: 1.25rem;
	}

	.btn-orange:hover {
		background-color: darken(@color4, 5%);
		border-color: darken(@color4, 5%);
		color: white;
	}

	.quick-links {
		display: flex;
		flex-wrap: wrap;
		justify-content: center;
		gap: 1rem 1.4rem;
	}

	.quick-links a {
		color: darken(@color9, 4%);
		font-weight: 700;
		text-decoration: none;
		border-bottom: 2px solid fade(@color9, 18%);
		padding-bottom: 0.1rem;
	}

	.quick-links a:hover {
		color: @color4;
		border-bottom-color: fade(@color4, 45%);
	}

	.floating {
		position: absolute;
		z-index: 1;
		animation: bob 5.8s ease-in-out infinite;
		opacity: 0.98;
		pointer-events: none;
	}

	.floating img {
		display: block;
		filter: drop-shadow(0 18px 30px rgba(0, 0, 0, 0.22));
	}

	.floating-left {
		left: 3.5%;
		top: 14%;
	}

	.floating-left img {
		width: min(21vw, 190px);
		transform: rotate(-13deg);
	}

	.floating-right {
		right: 4.5%;
		bottom: 11%;
		animation-delay: -2.3s;
	}

	.floating-right img {
		width: min(22vw, 200px);
		transform: rotate(11deg);
	}

	@keyframes bob {
		0%,
		100% {
			transform: translateY(0px);
		}
		50% {
			transform: translateY(-12px);
		}
	}

	@media (max-width: 768px) {
		.not-found-shell {
			padding-top: 1.5rem;
			padding-bottom: 1.5rem;
		}

		.not-found-card {
			padding: 2rem 1.15rem;
			border-radius: 26px;
		}

		.floating-left {
			left: -4%;
			top: 7%;
		}

		.floating-right {
			right: -5%;
			bottom: 7%;
		}

		.quick-links {
			gap: 0.8rem 1rem;
		}
	}
</style>
